// GenerateMvpDialog — one-click "Generate MVP" flow.
//
// Collects a tight 4-field brief (idea, target pages, tech preference,
// audience + monetization), calls the `mvp-spec` edge function to stream
// back a structured plan, and offers handoff buttons that deep-link the
// founder into an external builder (Lovable.dev / v0.dev / Bolt.new) with
// the generated builder prompt pre-filled in the URL hash so it lands on
// the new project immediately.
//
// Why hash-prefill and not querystring: Lovable.dev's deep-link contract
// uses `#prompt=` and v0/Bolt accept query params. We URL-encode + length-
// cap to ~6KB so the link stays under common browser limits.
//
// The component is self-contained — no parent state required beyond
// open/close. The spec is also returned via `onSpec` so the surrounding
// page (Agents Builder, /chat) can save it as an artifact if it wants.
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import {
  Wand2,
  Loader2,
  X,
  Plus,
  ExternalLink,
  Copy,
  Check,
  Sparkles,
  AlertCircle,
  Rocket,
  Globe2,
  Lock,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { streamSSE } from "@/lib/streamSSE";
import { toastError } from "@/lib/errorToast";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  useCreateBuild,
  useSaveBuildResult,
  usePublishBuild,
} from "@/hooks/useAiBuilder";
import {
  useCreateMvpBrief,
  useUpdateMvpBrief,
  useMvpBriefs,
  useDeleteMvpBrief,
  type MvpBrief,
} from "@/hooks/useMvpBriefs";
import { feedBuildStream, newBuildState } from "@/lib/parseBuildStream";
import { History, Trash2, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Optional pre-filled idea (e.g. coming from a chat composer draft). */
  initialIdea?: string;
  /** Fired once the spec finishes streaming so callers can persist it. */
  onSpec?: (markdown: string, brief: BriefForm) => void;
  /** Fired whenever the build lifecycle state changes so the surrounding
   *  page can mirror status (queued/running/ready/failed) outside the
   *  dialog — e.g. a strip under the "Generate MVP" button. */
  onBuildStatus?: (status: BuildStatusUpdate) => void;
  /** Optional brief to prefill the wizard with (from history "Reuse"). */
  prefill?: {
    idea: string;
    pages: string[];
    techPref: string;
    audience: string;
    monetization: string;
  } | null;
}

export type BuildStatusUpdate = {
  phase: "idle" | "generating" | "publishing" | "ready" | "error";
  progress: string;
  buildId: string | null;
  slug: string | null;
  liveUrl: string | null;
  error: string | null;
};

const TECH_OPTIONS = [
  { id: "ai-pick", label: "Let the AI pick" },
  { id: "react-supabase", label: "React + Supabase (default)" },
  { id: "nextjs-postgres", label: "Next.js + Postgres" },
  { id: "vite-sqlite", label: "Vite + SQLite (offline-friendly)" },
] as const;

/** Curated set of pages founders almost always want at MVP scope.
 *  Keeping the list short (<=12) so the picker stays scannable; rare
 *  pages stay reachable through the free-text input below. Labels are
 *  the canonical title we feed the model — the helper text under each
 *  is purely UI hinting. */
const PAGE_PRESETS: { label: string; hint: string }[] = [
  { label: "Landing", hint: "Marketing home + CTA" },
  { label: "Auth", hint: "Sign in / sign up" },
  { label: "Dashboard", hint: "Logged-in home" },
  { label: "Onboarding", hint: "First-run setup" },
  { label: "Settings", hint: "Profile + preferences" },
  { label: "Pricing", hint: "Plans + checkout entry" },
  { label: "Admin", hint: "Internal moderation" },
  { label: "Billing", hint: "Invoices + plan" },
  { label: "Detail", hint: "Single-record view" },
  { label: "Search", hint: "Browse + filter" },
  { label: "Notifications", hint: "Activity feed" },
  { label: "Help", hint: "Docs / FAQ" },
];

const briefSchema = z.object({
  idea: z
    .string()
    .trim()
    .min(10, "Describe the idea in at least a sentence")
    .max(1500, "Keep it under 1500 characters"),
  pages: z.array(z.string().min(1).max(40)).max(12),
  techPref: z.string().max(80),
  audience: z.string().trim().max(200),
  monetization: z.string().trim().max(80),
});

export type BriefForm = z.infer<typeof briefSchema>;

/** Build a deep link for the named external builder with the handoff
 *  prompt pre-filled. Each builder has a slightly different contract;
 *  we keep the mapping centralised so adding a new target is one entry. */
const buildHandoffUrl = (target: "lovable" | "v0" | "bolt", prompt: string): string => {
  // Cap at ~6KB to keep the URL under common browser limits — most
  // builder prompts comfortably fit, but pasting a giant brief would
  // silently truncate otherwise.
  const safe = prompt.slice(0, 6000);
  const encoded = encodeURIComponent(safe);
  switch (target) {
    case "lovable":
      // Lovable.dev reads `#prompt=` on the new-project entry.
      return `https://lovable.dev/?prompt=${encoded}`;
    case "v0":
      return `https://v0.dev/chat?q=${encoded}`;
    case "bolt":
      // Bolt.new accepts the prompt as a path segment.
      return `https://bolt.new/?prompt=${encoded}`;
  }
};

/** Pull the contents of the first ```text fenced block out of the spec
 *  markdown. That's the "Builder handoff prompt" the model emitted. */
const extractHandoffPrompt = (md: string): string => {
  const m = md.match(/```(?:text)?\s*\n([\s\S]*?)```/);
  return m ? m[1].trim() : "";
};

const GenerateMvpDialog = ({
  open,
  onClose,
  initialIdea,
  onSpec,
  onBuildStatus,
  prefill,
}: Props) => {
  const { user } = useAuth();
  const createBuild = useCreateBuild();
  const saveResult = useSaveBuildResult();
  const publish = usePublishBuild();
  const createBrief = useCreateMvpBrief();
  const updateBrief = useUpdateMvpBrief();
  const deleteBrief = useDeleteMvpBrief();
  const { data: history = [] } = useMvpBriefs();

  // The brief row id, set the moment we successfully insert. We patch
  // it later with the streamed spec + build linkage so the row is the
  // single durable record of "what I asked for and what came out".
  const [briefRowId, setBriefRowId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const [idea, setIdea] = useState(initialIdea ?? "");
  const [pageInput, setPageInput] = useState("");
  const [pages, setPages] = useState<string[]>([]);
  const [techPref, setTechPref] = useState<string>(TECH_OPTIONS[0].id);
  const [audience, setAudience] = useState("");
  const [monetization, setMonetization] = useState("");

  const [streaming, setStreaming] = useState(false);
  const [spec, setSpec] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Build-on-HackerLabs lifecycle: idle → generating → publishing → ready.
  // Kept separate from the spec-streaming state so the user can watch the
  // plan render and *then* opt in to the build, rather than us hijacking
  // the wizard with a long-running build the moment the plan finishes.
  const [buildPhase, setBuildPhase] = useState<
    "idle" | "generating" | "publishing" | "ready" | "error"
  >("idle");
  const [buildSlug, setBuildSlug] = useState<string | null>(null);
  const [buildId, setBuildId] = useState<string | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [buildProgress, setBuildProgress] = useState<string>("");
  const buildAbortRef = useRef<AbortController | null>(null);

  // Mirror lifecycle changes out to the parent so the Play page (or any
  // host) can render its own status strip without re-opening the dialog.
  useEffect(() => {
    if (!onBuildStatus) return;
    const liveUrl = buildSlug ? `${window.location.origin}/sites/${buildSlug}` : null;
    onBuildStatus({
      phase: buildPhase,
      progress: buildProgress,
      buildId,
      slug: buildSlug,
      liveUrl,
      error: buildError,
    });
  }, [buildPhase, buildProgress, buildId, buildSlug, buildError, onBuildStatus]);

  // Reset state when the dialog reopens — but keep `initialIdea` (or a
  // history `prefill`) so a chat handoff / reuse doesn't lose the draft.
  useEffect(() => {
    if (!open) return;
    if (prefill) {
      setIdea(prefill.idea);
      setPages(prefill.pages);
      const matched = TECH_OPTIONS.find((t) => t.label === prefill.techPref)?.id;
      setTechPref(matched ?? (prefill.techPref || TECH_OPTIONS[0].id));
      setAudience(prefill.audience);
      setMonetization(prefill.monetization);
    } else {
      setIdea(initialIdea ?? "");
    }
    setSpec("");
    setErrorMsg(null);
    setStreaming(false);
    setBriefRowId(null);
    setShowHistory(false);
    abortRef.current?.abort();
    abortRef.current = null;
    // Reset build state too so reopening a closed dialog starts clean.
    buildAbortRef.current?.abort();
    buildAbortRef.current = null;
    setBuildPhase("idle");
    setBuildSlug(null);
    setBuildId(null);
    setBuildError(null);
    setBuildProgress("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handoffPrompt = useMemo(() => extractHandoffPrompt(spec), [spec]);

  const addPage = () => {
    const v = pageInput.trim();
    if (!v || v.length > 40) return;
    if (pages.includes(v)) return;
    if (pages.length >= 12) {
      toast("Cap is 12 pages — drop one to add another");
      return;
    }
    setPages([...pages, v]);
    setPageInput("");
  };

  const removePage = (p: string) => setPages(pages.filter((x) => x !== p));

  /** Case-insensitive membership check so "landing" entered manually
   *  still highlights the Landing preset — keeps the UI honest about
   *  what's actually in the brief. */
  const hasPage = (label: string): boolean =>
    pages.some((p) => p.toLowerCase() === label.toLowerCase());

  /** Toggle a preset in the pages list. We respect the same 12-cap as
   *  the free-text path so the model never gets a sprawling brief. */
  const togglePreset = (label: string) => {
    if (hasPage(label)) {
      // Remove whichever cased variant exists.
      setPages(pages.filter((p) => p.toLowerCase() !== label.toLowerCase()));
      return;
    }
    if (pages.length >= 12) {
      toast("Cap is 12 pages — drop one to add another");
      return;
    }
    setPages([...pages, label]);
  };

  const handleGenerate = async () => {
    setErrorMsg(null);
    const techLabel = TECH_OPTIONS.find((t) => t.id === techPref)?.label ?? techPref;
    const formData: BriefForm = {
      idea: idea.trim(),
      pages,
      techPref: techLabel,
      audience: audience.trim(),
      monetization: monetization.trim(),
    };
    const parsed = briefSchema.safeParse(formData);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setErrorMsg(first?.message ?? "Brief is invalid");
      return;
    }

    setSpec("");
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    let acc = "";
    try {
      // Persist the brief upfront if signed in — gives us a row id to
      // patch with the streamed spec and any subsequent build linkage,
      // so history captures even abandoned runs (handy for "regenerate").
      let rowId = briefRowId;
      if (user && !rowId) {
        try {
          const row = await createBrief.mutateAsync({
            idea: parsed.data.idea,
            pages: parsed.data.pages,
            tech_pref: parsed.data.techPref,
            audience: parsed.data.audience,
            monetization: parsed.data.monetization,
          });
          rowId = row.id;
          setBriefRowId(row.id);
        } catch (e) {
          // Non-fatal — generation still works without history.
          console.warn("[mvp] failed to save brief", e);
        }
      }
      await streamSSE({
        url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mvp-spec`,
        body: parsed.data,
        onDelta: (chunk) => {
          acc += chunk;
          setSpec(acc);
        },
        signal: controller.signal,
      });
      if (!acc.trim()) throw new Error("Empty spec returned");
      onSpec?.(acc, parsed.data);
      // Persist the streamed spec back to the brief so it shows up in
      // history with the full markdown, not just the form inputs.
      if (rowId) {
        updateBrief.mutate({ id: rowId, patch: { spec_markdown: acc } });
      }
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      const msg = e instanceof Error ? e.message : "MVP generation failed";
      setErrorMsg(msg);
      toastError(msg, { title: "Generate MVP failed" });
    } finally {
      setStreaming(false);
    }
  };

  const handleCopy = async () => {
    const blob = handoffPrompt || spec;
    if (!blob) return;
    await navigator.clipboard.writeText(blob);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Copied — paste into any AI builder");
  };

  const openHandoff = (target: "lovable" | "v0" | "bolt") => {
    if (!handoffPrompt) {
      toast("Spec didn't include a handoff prompt — copy the full plan instead.");
      return;
    }
    const url = buildHandoffUrl(target, handoffPrompt);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const cancel = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  /** Compose the prompt we feed to ai-builder-generate. The MVP spec is
   *  great context for *humans*, but the builder model wants a concrete
   *  build instruction. We synthesise one from the structured brief
   *  (idea + pages + audience) so the generator produces a real site
   *  rather than re-restating the plan. */
  const composeBuilderPrompt = (): string => {
    const techLabel = TECH_OPTIONS.find((t) => t.id === techPref)?.label ?? "";
    const lines: string[] = [];
    lines.push(idea.trim());
    if (pages.length) {
      lines.push("", `Include these pages/sections: ${pages.join(", ")}.`);
    }
    if (audience.trim()) lines.push("", `Target audience: ${audience.trim()}.`);
    if (monetization.trim()) lines.push(`Monetization: ${monetization.trim()}.`);
    if (techPref && techPref !== "ai-pick" && techLabel) {
      lines.push(`Preferred stack: ${techLabel}.`);
    }
    lines.push(
      "",
      "Make it visually polished — modern typography, real (not lorem) copy, and a thoughtful color palette.",
    );
    return lines.join("\n").slice(0, 4000);
  };

  const handleBuildOnHackerlabs = async () => {
    if (!user) {
      toast.error("Sign in to build on HackerLabs.");
      return;
    }
    setBuildError(null);
    setBuildPhase("generating");
    setBuildProgress("Spinning up the generator…");
    const ctrl = new AbortController();
    buildAbortRef.current = ctrl;
    let createdId: string | null = null;
    try {
      const builderPrompt = composeBuilderPrompt();
      const created = await createBuild.mutateAsync({
        prompt: builderPrompt,
        title: idea.slice(0, 60),
      });
      createdId = created.id;
      setBuildId(created.id);
      // Link the running build to the saved brief so history shows
      // status without requiring an extra join client-side.
      if (briefRowId) {
        updateBrief.mutate({
          id: briefRowId,
          patch: { build_id: created.id, build_status: "generating" },
        });
      }

      // Stream from ai-builder-generate. Reuse the same parser the
      // /builder page uses so the marker protocol stays single-source.
      // Send the user's session JWT (not the anon key) — the edge
      // function authenticates the caller and meters their plan.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Sign in required to generate an MVP.");
      }
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/ai-builder-generate`, {
        method: "POST",
        signal: ctrl.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ prompt: builderPrompt }),
      });
      if (!resp.ok || !resp.body) {
        const txt = await resp.text().catch(() => "");
        throw new Error(txt || `Generator failed (${resp.status})`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      const state = newBuildState();
      let lastFile = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        feedBuildStream(state, chunk);
        // Surface a small progress hint so a 30-60s build doesn't feel
        // dead. We swap the message whenever the active file changes.
        if (state.activePath && state.activePath !== lastFile) {
          lastFile = state.activePath;
          setBuildProgress(`Writing ${lastFile}…`);
        } else if (!state.activePath && state.kind && !lastFile) {
          setBuildProgress(`Drafting ${state.kind === "react" ? "React app" : "site"}…`);
        }
      }

      if (state.files.length === 0) throw new Error("Generator returned no files");

      await saveResult.mutateAsync({
        buildId: created.id,
        kind: state.kind ?? "html",
        title: state.title || idea.slice(0, 60),
        files: state.files,
      });

      setBuildPhase("publishing");
      setBuildProgress("Publishing to hackerlabs.ai…");
      const pub = await publish.mutateAsync({
        buildId: created.id,
        action: "publish",
      });
      if (!pub.slug) throw new Error("Publish did not return a slug");
      setBuildSlug(pub.slug);
      setBuildPhase("ready");
      setBuildProgress("");
      if (briefRowId) {
        updateBrief.mutate({
          id: briefRowId,
          patch: { build_slug: pub.slug, build_status: "ready" },
        });
      }
      toast.success("Your MVP is live on HackerLabs.");
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        setBuildPhase("idle");
        setBuildProgress("");
        return;
      }
      const msg = e instanceof Error ? e.message : "Build failed";
      console.error("[GenerateMvpDialog] build failed", e);
      setBuildError(msg);
      setBuildPhase("error");
      setBuildProgress("");
      if (briefRowId) {
        updateBrief.mutate({
          id: briefRowId,
          patch: { build_status: "error" },
        });
      }
      toastError(msg, { title: "Build failed" });
    } finally {
      buildAbortRef.current = null;
    }
  };

  const cancelBuild = () => {
    buildAbortRef.current?.abort();
    setBuildPhase("idle");
    setBuildProgress("");
  };

  const buildIsRunning = buildPhase === "generating" || buildPhase === "publishing";
  const liveUrl = buildSlug ? `${window.location.origin}/sites/${buildSlug}` : null;

  const copyLiveUrl = async () => {
    if (!liveUrl) return;
    await navigator.clipboard.writeText(liveUrl);
    toast.success("Live URL copied.");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-primary" />
            Generate MVP
            {user && history.length > 0 && (
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-card/40 text-[11px] font-mono text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
              >
                <History className="w-3.5 h-3.5" />
                {showHistory ? "Hide history" : `History (${history.length})`}
              </button>
            )}
          </DialogTitle>
          <p className="text-xs font-mono text-muted-foreground">
            Describe the idea — get a stack pick, page list, schema sketch,
            week-1 cut list, and a handoff prompt you can paste into any
            builder.
          </p>
        </DialogHeader>

        {/* Saved-brief history — shown above the form when toggled. Each
            row reuses the brief into the form (so the user can tweak and
            re-run) and links straight to the live build if it shipped. */}
        {showHistory && user && (
          <BriefHistoryList
            briefs={history}
            onReuse={(b) => {
              setIdea(b.idea);
              setPages(b.pages || []);
              const matched = TECH_OPTIONS.find((t) => t.label === b.tech_pref)?.id;
              setTechPref(matched ?? (b.tech_pref || TECH_OPTIONS[0].id));
              setAudience(b.audience || "");
              setMonetization(b.monetization || "");
              setSpec("");
              setBriefRowId(null);
              setShowHistory(false);
              toast.success("Brief loaded — tweak and regenerate.");
            }}
            onDelete={(id) => deleteBrief.mutate(id)}
          />
        )}

        {/* Brief form — hidden once we have a streamed spec to keep
            the result the centre of attention. */}
        {!spec && !streaming && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="mvp-idea" className="text-xs font-mono font-semibold text-foreground">
                Idea (1-3 sentences) <span className="text-destructive">*</span>
              </label>
              <Textarea
                id="mvp-idea"
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                rows={3}
                placeholder="A flashcards app that turns any pasted article into a 10-card spaced-repetition deck for high-school students."
                maxLength={1500}
                className="text-sm font-mono resize-y"
              />
              <div className="text-[10px] font-mono text-muted-foreground text-right">
                {idea.length}/1500
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-mono font-semibold text-foreground">
                Target pages / screens
              </label>
              {/* Quick-pick grid — common MVP pages users can multi-select
                  with one tap. Toggling here adds/removes the canonical
                  label from `pages`; anything not in this list still flows
                  through the custom input below. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {PAGE_PRESETS.map((preset) => {
                  const selected = hasPage(preset.label);
                  return (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => togglePreset(preset.label)}
                      aria-pressed={selected}
                      className={
                        "group flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors " +
                        (selected
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border bg-card/40 text-muted-foreground hover:border-primary/40 hover:text-foreground")
                      }
                    >
                      <span
                        className={
                          "mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border " +
                          (selected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background")
                        }
                        aria-hidden
                      >
                        {selected && <Check className="h-2.5 w-2.5" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[11px] font-mono font-semibold leading-tight">
                          {preset.label}
                        </span>
                        <span className="block truncate text-[10px] font-mono text-muted-foreground/80">
                          {preset.hint}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Input
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addPage();
                    }
                  }}
                  placeholder="Add a custom page (e.g. Changelog, Roadmap)…"
                  maxLength={40}
                  className="text-sm font-mono"
                />
                <Button type="button" variant="outline" onClick={addPage} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {pages.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {pages.length}/12 selected:
                  </span>
                  {pages.map((p) => (
                    <Badge key={p} variant="secondary" className="font-mono text-[11px] gap-1">
                      {p}
                      <button
                        type="button"
                        onClick={() => removePage(p)}
                        aria-label={`Remove ${p}`}
                        className="hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-[10px] font-mono text-muted-foreground">
                Tap presets to multi-select, or type a custom page and press Enter. Skip entirely to let the AI propose pages.
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="mvp-tech" className="text-xs font-mono font-semibold text-foreground">
                Tech preference
              </label>
              <select
                id="mvp-tech"
                value={techPref}
                onChange={(e) => setTechPref(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm font-mono focus:outline-none focus:border-primary"
              >
                {TECH_OPTIONS.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="mvp-aud" className="text-xs font-mono font-semibold text-foreground">
                  Audience (optional)
                </label>
                <Input
                  id="mvp-aud"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="High-school students cramming for exams"
                  maxLength={200}
                  className="text-sm font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="mvp-mon" className="text-xs font-mono font-semibold text-foreground">
                  Monetization (optional)
                </label>
                <Input
                  id="mvp-mon"
                  value={monetization}
                  onChange={(e) => setMonetization(e.target.value)}
                  placeholder="Free + $5/mo Pro"
                  maxLength={80}
                  className="text-sm font-mono"
                />
              </div>
            </div>

            {errorMsg && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs font-mono text-destructive">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={idea.trim().length < 10}>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate MVP
              </Button>
            </div>
          </div>
        )}

        {/* Streaming + result view */}
        {(streaming || spec) && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-mono text-primary inline-flex items-center gap-1.5">
                {streaming ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Drafting your MVP plan…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    MVP plan ready
                  </>
                )}
              </div>
              {streaming && (
                <Button size="sm" variant="ghost" onClick={cancel}>
                  Stop
                </Button>
              )}
            </div>

            <div className="rounded-md border border-border bg-card/60 p-4 max-h-[55vh] overflow-y-auto">
              <div className="prose prose-sm dark:prose-invert max-w-none font-mono text-xs leading-relaxed [&_h2]:text-[12px] [&_h2]:font-bold [&_h2]:text-primary [&_h2]:uppercase [&_h2]:tracking-wider [&_h2]:mt-4 [&_h2]:mb-1.5 [&_h2:first-child]:mt-0 [&_p]:my-1 [&_code]:text-primary [&_pre]:bg-background/60 [&_pre]:border [&_pre]:border-border [&_pre]:rounded [&_pre]:p-2 [&_pre]:text-[11px]">
                {spec ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{spec || "▍"}</ReactMarkdown>
                ) : (
                  <p className="text-muted-foreground">Warming up the architect…</p>
                )}
              </div>
            </div>

            {!streaming && spec && (
              <div className="space-y-2">
                {/* Primary CTA — build it inside HackerLabs and surface
                    the live URL right here, no external builders required.
                    Auth-gated because builds are saved per-user. */}
                <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] font-mono font-bold text-primary inline-flex items-center gap-1.5">
                      <Rocket className="w-3.5 h-3.5" />
                      Build it on HackerLabs
                    </div>
                    {buildPhase === "ready" && liveUrl && (
                      <Badge variant="secondary" className="font-mono text-[10px] gap-1">
                        <Globe2 className="w-3 h-3" /> Live
                      </Badge>
                    )}
                  </div>

                  {buildPhase === "idle" && (
                    <>
                      <p className="text-[11px] font-mono text-muted-foreground">
                        We'll generate the site from your brief and publish it to{" "}
                        <code className="text-primary">hackerlabs.ai/sites/…</code>{" "}
                        in about a minute. No external accounts needed.
                      </p>
                      {!user ? (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1">
                            <Lock className="w-3 h-3" /> Sign in required
                          </span>
                          <Button size="sm" onClick={() => toast("Sign in from the navbar to build.")}>
                            Sign in to build
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          onClick={handleBuildOnHackerlabs}
                          className="w-full justify-center"
                        >
                          <Rocket className="w-3.5 h-3.5 mr-1.5" />
                          Build &amp; publish on HackerLabs
                        </Button>
                      )}
                    </>
                  )}

                  {buildIsRunning && (
                    <>
                      <div className="flex items-center gap-2 text-[11px] font-mono text-foreground">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                        {buildProgress || (buildPhase === "publishing"
                          ? "Publishing…"
                          : "Generating files…")}
                      </div>
                      <Button size="sm" variant="ghost" onClick={cancelBuild} className="w-full">
                        Cancel build
                      </Button>
                    </>
                  )}

                  {buildPhase === "ready" && liveUrl && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 rounded-md border border-border bg-background/60 p-2">
                        <Globe2 className="w-3.5 h-3.5 text-primary shrink-0" />
                        <a
                          href={liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 text-[11px] font-mono text-primary truncate hover:underline"
                          title={liveUrl}
                        >
                          {liveUrl}
                        </a>
                        <button
                          type="button"
                          onClick={copyLiveUrl}
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Copy live URL"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button asChild size="sm" variant="default" className="justify-center">
                          <a href={liveUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                            Open site
                          </a>
                        </Button>
                        {buildId && (
                          <Button asChild size="sm" variant="outline" className="justify-center">
                            <Link to={`/builder?b=${buildId}`} onClick={onClose}>
                              <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                              Edit in Builder
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {buildPhase === "error" && (
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 text-[11px] font-mono text-destructive">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>{buildError || "Build failed."}</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={handleBuildOnHackerlabs} className="w-full">
                        Retry build
                      </Button>
                    </div>
                  )}
                </div>

                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                  Or hand off to an external builder
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Button
                    onClick={() => openHandoff("lovable")}
                    disabled={!handoffPrompt}
                    className="justify-center"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    Open in Lovable
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => openHandoff("v0")}
                    disabled={!handoffPrompt}
                    className="justify-center"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    Open in v0.dev
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => openHandoff("bolt")}
                    disabled={!handoffPrompt}
                    className="justify-center"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    Open in Bolt.new
                  </Button>
                </div>
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                  <Button variant="ghost" size="sm" onClick={handleCopy}>
                    {copied ? (
                      <Check className="w-3.5 h-3.5 mr-1.5 text-primary" />
                    ) : (
                      <Copy className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {copied ? "Copied" : "Copy handoff prompt"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setSpec(""); }}>
                    <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                    Tweak the brief
                  </Button>
                </div>
              </div>
            )}

            {errorMsg && !streaming && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs font-mono text-destructive">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GenerateMvpDialog;

/** Compact list of saved briefs. Each row exposes "Reuse" (load the
 *  brief back into the form) and "Open" (jump to the published site).
 *  We deliberately keep this dense — history is a side panel, not
 *  the main course. */
const BriefHistoryList = ({
  briefs,
  onReuse,
  onDelete,
}: {
  briefs: MvpBrief[];
  onReuse: (b: MvpBrief) => void;
  onDelete: (id: string) => void;
}) => {
  if (briefs.length === 0) return null;
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const statusBadge = (s: string) => {
    const map: Record<string, { label: string; tone: string }> = {
      draft: { label: "Draft", tone: "bg-muted text-muted-foreground" },
      generating: { label: "Building", tone: "bg-primary/15 text-primary" },
      ready: { label: "Live", tone: "bg-primary/15 text-primary" },
      error: { label: "Failed", tone: "bg-destructive/15 text-destructive" },
    };
    const m = map[s] ?? map.draft;
    return (
      <span className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${m.tone}`}>
        {m.label}
      </span>
    );
  };
  return (
    <div className="rounded-md border border-border bg-card/40 p-2 space-y-1.5 max-h-[40vh] overflow-y-auto">
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-1">
        Saved briefs
      </div>
      {briefs.map((b) => (
        <div
          key={b.id}
          className="flex items-start gap-2 rounded-md border border-border bg-background/60 px-2 py-1.5 hover:border-primary/40 transition-colors"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {statusBadge(b.build_status)}
              <span className="text-[10px] font-mono text-muted-foreground">
                {fmt(b.created_at)}
              </span>
              {b.pages?.length > 0 && (
                <span className="text-[10px] font-mono text-muted-foreground">
                  · {b.pages.length} page{b.pages.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[12px] font-mono text-foreground line-clamp-2">
              {b.idea}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {b.build_slug && (
              <a
                href={`/sites/${b.build_slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 rounded text-muted-foreground hover:text-primary"
                title="Open live site"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <button
              type="button"
              onClick={() => onReuse(b)}
              className="p-1 rounded text-muted-foreground hover:text-primary"
              title="Load this brief into the form"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                if (confirm("Delete this saved brief?")) onDelete(b.id);
              }}
              className="p-1 rounded text-muted-foreground hover:text-destructive"
              title="Delete brief"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};